// ============================================================================
// CLOSURE REASON SERVICE
// ============================================================================

import { get, post, put, del } from '../client/axiosClient';
import { closureReasonEndpoints, CLOSURE_REASON_BASE_PATH } from '../endpoints/closureReason.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  PaginatedResponse,
  EmptyResponse,
  ID,
} from '../types';
import type {
  ClosureReason,
  ClosureReasonListItem,
  CreateClosureReasonRequest,
  UpdateClosureReasonRequest,
  ClosureReasonSearchRequest,
} from '../types/closureReason.types';

class ClosureReasonService {
  async getAll(params?: ClosureReasonSearchRequest): Promise<PaginatedResponse<ClosureReasonListItem>> {
    const endpoint = params
      ? buildEndpointWithQuery(CLOSURE_REASON_BASE_PATH, params)
      : closureReasonEndpoints.list();

    const response = await get<any>(endpoint);
    const raw = response.data;
    const pagedResult = raw?.data ?? {};
    return {
      success: raw?.success ?? false,
      message: raw?.message,
      data: pagedResult.items ?? [],
      pagination: {
        currentPage: pagedResult.page ?? 1,
        pageSize: pagedResult.pageSize ?? 20,
        totalPages: pagedResult.totalPages ?? 0,
        totalItems: pagedResult.totalCount ?? 0,
        hasNextPage: pagedResult.hasNext ?? false,
        hasPreviousPage: pagedResult.hasPrevious ?? false,
      },
    };
  }

  async getById(id: ID): Promise<ApiResponse<ClosureReason>> {
    const response = await get<ApiResponse<ClosureReason>>(closureReasonEndpoints.getById(id));
    return response.data;
  }

  async getByCode(code: string): Promise<ApiResponse<ClosureReason>> {
    const response = await get<ApiResponse<ClosureReason>>(closureReasonEndpoints.getByCode(code));
    return response.data;
  }

  async create(data: CreateClosureReasonRequest): Promise<ApiResponse<ClosureReason>> {
    const response = await post<ApiResponse<ClosureReason>>(closureReasonEndpoints.create(), data);
    return response.data;
  }

  async update(id: ID, data: UpdateClosureReasonRequest): Promise<ApiResponse<ClosureReason>> {
    const response = await put<ApiResponse<ClosureReason>>(closureReasonEndpoints.update(id), data);
    return response.data;
  }

  async delete(id: ID): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(closureReasonEndpoints.delete(id));
    return response.data;
  }

  async validateCode(code: string, excludeId?: ID): Promise<ApiResponse<boolean>> {
    const endpoint = excludeId
      ? `${closureReasonEndpoints.validateCode(code)}?excludeId=${excludeId}`
      : closureReasonEndpoints.validateCode(code);
    const response = await get<ApiResponse<boolean>>(endpoint);
    return response.data;
  }
}

export const closureReasonService = new ClosureReasonService();
export default closureReasonService;
